import { OnboardingAgent, AgentMessage, AgentContext, AgentQuestion } from './onboardingAgent.types.ts';
import { OnboardingV2State } from '../../store/onboardingV2/types.ts';

const getNextQuestion = (state: OnboardingV2State): AgentQuestion | null => {
  if (!state.accountType) {
    return {
      id: 'accountType',
      type: 'interactive',
      interactiveType: 'accountType',
    };
  }

  if (state.accountType === 'PERSON') {
    const form = state.personaForm;
    if (!form.name) {
      return {
        id: 'name',
        type: 'text',
        field: 'name',
        placeholder: 'Escribí tu nombre...',
      };
    }
    if (!form.lastName) {
      return {
        id: 'lastName',
        type: 'text',
        field: 'lastName',
        placeholder: 'Escribí tu apellido...',
      };
    }
    if (form.tel === '' || form.tel === undefined) {
      return {
        id: 'tel',
        type: 'text',
        field: 'tel',
        placeholder: 'Escribí tu teléfono...',
      };
    }
    if (form.webSite === '' || form.webSite === undefined) {
      return {
        id: 'webSite',
        type: 'text',
        field: 'webSite',
        placeholder: 'Escribí tu sitio web (opcional)...',
      };
    }
    if (form.bio === '' || form.bio === undefined) {
      return {
        id: 'bio',
        type: 'text',
        field: 'bio',
        placeholder: 'Contame sobre vos...',
      };
    }
  } else if (state.accountType === 'COMPANY') {
    const form = state.empresaForm;
    if (!form.companyName) {
      return {
        id: 'companyName',
        type: 'text',
        field: 'companyName',
        placeholder: 'Escribí el nombre de tu empresa...',
      };
    }
    if (form.tel === '' || form.tel === undefined) {
      return {
        id: 'tel',
        type: 'text',
        field: 'tel',
        placeholder: 'Escribí tu teléfono...',
      };
    }
    if (form.webSite === '' || form.webSite === undefined) {
      return {
        id: 'webSite',
        type: 'text',
        field: 'webSite',
        placeholder: 'Escribí tu sitio web (opcional)...',
      };
    }
    if (form.bio === '' || form.bio === undefined) {
      return {
        id: 'bio',
        type: 'text',
        field: 'bio',
        placeholder: 'Contame sobre tu empresa...',
      };
    }
  }

  if (state.userSkills.length === 0) {
    return {
      id: 'skills',
      type: 'interactive',
      interactiveType: 'skills',
    };
  }

  if (state.preferredProfileCategories.length === 0) {
    return {
      id: 'profiles',
      type: 'interactive',
      interactiveType: 'profiles',
    };
  }

  if (!state.allCountry && state.workZones.length === 0) {
    return {
      id: 'workZones',
      type: 'interactive',
      interactiveType: 'workZones',
    };
  }

  return null;
};

export const mockOnboardingAgent: OnboardingAgent = {
  async getWelcomeMessage(): Promise<AgentMessage> {
    return {
      content: '¡Hola! Soy tu asistente de Edify. Te voy a ayudar a crear tu perfil paso a paso. Primero, contame qué tipo de cuenta querés crear.',
      question: {
        id: 'accountType',
        type: 'interactive',
        interactiveType: 'accountType',
      },
    };
  },

  async getResumeMessage(state: OnboardingV2State): Promise<AgentMessage> {
    const nextQuestion = getNextQuestion(state);
    
    if (!nextQuestion) {
      return {
        content: '¡Bienvenido de vuelta! Parece que ya completaste tu perfil. Podés revisar o cambiar pasos, o finalizar el onboarding para guardar tu perfil.',
        question: {
          id: 'completeActions',
          type: 'interactive',
          interactiveType: 'completeActions',
        },
      };
    }

    if (nextQuestion.id === 'accountType') {
      return {
        content: '¡Bienvenido de vuelta! Continuemos con tu perfil. Necesito saber qué tipo de cuenta querés crear.',
        question: nextQuestion,
      };
    }

    if (state.accountType === 'PERSON') {
      const form = state.personaForm;
      if (!form.name) {
        return {
          content: '¡Bienvenido de vuelta! Continuemos con tu perfil profesional. Escribí tu nombre',
          question: {
            id: 'name',
            type: 'text',
            field: 'name',
            placeholder: 'Escribí tu nombre...',
          },
        };
      }
      if (!form.lastName) {
        return {
          content: `¡Hola ${form.name}! Continuemos. Escribí tu apellido`,
          question: {
            id: 'lastName',
            type: 'text',
            field: 'lastName',
            placeholder: 'Escribí tu apellido...',
          },
        };
      }
      if (form.tel === '' || form.tel === undefined) {
        return {
          content: '¿Querés sumar un teléfono de contacto? Esto facilita que otros perfiles se comuniquen con vos. Si no, podés hacer clic en “Omitir” y continuar.',
          question: {
            id: 'tel',
            type: 'text',
            field: 'tel',
            placeholder: 'Escribí tu teléfono (solo números)...',
          },
        };
      }
      if (form.webSite === '' || form.webSite === undefined) {
        return {
          content: '¿Tenés un sitio web o portfolio? Podés agregarlo ahora o más adelante.',
          question: {
            id: 'webSite',
            type: 'text',
            field: 'webSite',
            placeholder: 'Escribí tu sitio web (opcional)...',
          },
        };
      }
      if (form.bio === '' || form.bio === undefined) {
        return {
          content: 'Contame sobre vos. Escribí qué hacés y cuál es tu experiencia. Esto ayudará a otros usuarios a conocerte mejor. Si preferís, podés hacer clic en "Omitir" para continuar',
          question: {
            id: 'bio',
            type: 'text',
            field: 'bio',
            placeholder: 'Contame sobre vos...',
          },
        };
      }
    } else if (state.accountType === 'COMPANY') {
      const form = state.empresaForm;
      if (!form.companyName) {
        return {
          content: '¡Bienvenido de vuelta! Continuemos con tu cuenta empresarial. Escribí el nombre de tu empresa',
          question: {
            id: 'companyName',
            type: 'text',
            field: 'companyName',
            placeholder: 'Escribí el nombre de tu empresa...',
          },
        };
      }
      if (form.tel === '' || form.tel === undefined) {
        return {
          content: '¿Querés sumar un teléfono de contacto? Esto facilita que otros perfiles se comuniquen con vos. Si no, podés hacer clic en “Omitir” y continuar.',
          question: {
            id: 'tel',
            type: 'text',
            field: 'tel',
            placeholder: 'Escribí tu teléfono (solo números)...',
          },
        };
      }
      if (form.webSite === '' || form.webSite === undefined) {
        return {
          content: '¿Tenés un sitio web o portfolio? Podés agregarlo ahora o más adelante.',
          question: {
            id: 'webSite',
            type: 'text',
            field: 'webSite',
            placeholder: 'Escribí tu sitio web (opcional)...',
          },
        };
      }
      if (form.bio === '' || form.bio === undefined) {
        return {
          content: 'Contame sobre tu empresa. Escribí qué hace tu empresa y cuál es su experiencia. Esto ayudará a otros usuarios a conocerte mejor.',
          question: {
            id: 'bio',
            type: 'text',
            field: 'bio',
            placeholder: 'Contame sobre tu empresa...',
          },
        };
      }
    }

    if (state.userSkills.length === 0) {
      return {
        content: 'Continuemos con tus habilidades. Buscá y agregá las que correspondan.',
        question: {
          id: 'skills',
          type: 'interactive',
          interactiveType: 'skills',
        },
      };
    }

    if (state.preferredProfileCategories.length === 0) {
      return {
        content: '¿Con qué tipo de perfiles te interesa conectar? Esto define qué vas a ver y quiénes pueden encontrarte.',
        question: {
          id: 'profiles',
          type: 'interactive',
          interactiveType: 'profiles',
        },
      };
    }

    if (state.userSkills.length === 0) {
      return {
        content: '¡Bienvenido de vuelta! Continuemos con tu perfil. Ahora vamos a seleccionar tus habilidades. Escribí en el buscador las habilidades que tenés y seleccioná las que correspondan.',
        question: nextQuestion,
      };
    }

    if (!state.allCountry && state.workZones.length === 0) {
      return {
        content: '¡Bienvenido de vuelta! Continuemos. ¿En qué zonas trabajás? Seleccioná si comercializás en todo el país o en zonas específicas.',
        question: nextQuestion,
      };
    }

    return {
      content: '¡Bienvenido de vuelta! Parece que ya completaste tu perfil. Podés revisar o cambiar pasos, o finalizar el onboarding para guardar tu perfil.',
      question: {
        id: 'completeActions',
        type: 'interactive',
        interactiveType: 'completeActions',
      },
    };
  },

  async getResponse(context: AgentContext): Promise<AgentMessage> {
    const { state, lastAction } = context;

    if (lastAction?.startsWith('skip_')) {
      const nextQuestion = getNextQuestion(state);
      if (!nextQuestion) {
        return {
          content: '¡Perfecto! Ya tenemos tu información. Ahora, podés revisar o cambiar pasos. Si ves todo bien, podés finalizar el onboarding.',
          question: {
            id: 'completeActions',
            type: 'interactive',
            interactiveType: 'completeActions',
          },
        };
      }

      if (state.accountType === 'PERSON') {
        const form = state.personaForm;
        if (nextQuestion.id === 'name') {
          return {
            content: 'Escribí tu nombre',
            question: nextQuestion,
          };
        }
        if (nextQuestion.id === 'lastName') {
          return {
            content: `¡Gracias ${form.name}! Ahora escribí tu apellido`,
            question: nextQuestion,
          };
        }
        if (nextQuestion.id === 'tel') {
          return {
            content: '¿Querés sumar un teléfono de contacto? Esto facilita que otros perfiles se comuniquen con vos. Si no, podés hacer clic en “Omitir” y continuar.',
            question: nextQuestion,
          };
        }
        if (nextQuestion.id === 'webSite') {
          return {
            content: '¿Tenés un sitio web o portfolio? Podés agregarlo ahora o más adelante.',
            question: nextQuestion,
          };
        }
        if (nextQuestion.id === 'bio') {
          return {
            content: 'Contame sobre vos. Escribí qué hacés y cuál es tu experiencia. Esto ayudará a otros usuarios a conocerte mejor. Si preferís, podés hacer clic en "Omitir" para continuar',
            question: nextQuestion,
          };
        }
      } else if (state.accountType === 'COMPANY') {
        if (nextQuestion.id === 'companyName') {
          return {
            content: 'Escribí el nombre de tu empresa',
            question: nextQuestion,
          };
        }
        if (nextQuestion.id === 'tel') {
          return {
            content: '¿Querés sumar un teléfono de contacto? Esto facilita que otros perfiles se comuniquen con vos. Si no, podés hacer clic en “Omitir” y continuar.',
            question: nextQuestion,
          };
        }
        if (nextQuestion.id === 'webSite') {
          return {
            content: '¿Tenés un sitio web o portfolio? Podés agregarlo ahora o más adelante.',
            question: nextQuestion,
          };
        }
        if (nextQuestion.id === 'bio') {
          return {
            content: 'Contame sobre tu empresa. Escribí qué hace tu empresa y cuál es su experiencia. Esto ayudará a otros usuarios a conocerte mejor. Si preferís, podés hacer clic en "Omitir" para continuar',
            question: nextQuestion,
          };
        }
      }

      if (nextQuestion.id === 'skills') {
        return {
          content: '¡Excelente! Ahora vamos a seleccionar tus habilidades. Esto ayuda a que te encuentren los perfiles correctos.Podés buscar y elegir todas las que apliquen.',
          question: nextQuestion,
        };
      }

      if (nextQuestion.id === 'workZones') {
        return {
          content: '¿En qué zonas trabajás? Seleccioná si comercializás en todo el país o en zonas específicas.',
          question: nextQuestion,
        };
      }

      return {
        content: 'Perfecto, continuemos',
        question: nextQuestion,
      };
    }

    if (lastAction === 'accountType') {
      const isPerson = state.accountType === 'PERSON';
      return {
          content: isPerson
          ? 'Perfecto, vamos a crear tu perfil profesional. Empecemos por lo básico. ¿Cómo te llamás?'
          : 'Excelente, vamos a configurar tu cuenta empresarial. Necesito algunos datos. Escribí el nombre de tu empresa',
        question: isPerson
          ? {
              id: 'name',
              type: 'text',
              field: 'name',
              placeholder: 'Escribí tu nombre...',
            }
          : {
              id: 'companyName',
              type: 'text',
              field: 'companyName',
              placeholder: 'Escribí el nombre de tu empresa...',
            },
      };
    }

    if (lastAction === 'skill') {
      return {
        content: 'Habilidad agregada correctamente. Podés seguir agregando más habilidades escribiendo en el buscador o hacer clic en "Continuar" cuando termines.',
        question: {
          id: 'skills',
          type: 'interactive',
          interactiveType: 'skills',
        },
      };
    }

    if (lastAction === 'skillsComplete') {
      return {
        content: '¿Con qué tipo de perfiles te interesa conectar? Esto define qué vas a ver y quiénes pueden encontrarte.',
        question: {
          id: 'profiles',
          type: 'interactive',
          interactiveType: 'profiles',
        },
      };
    }

    if (lastAction === 'profilesComplete') {
      return {
        content: 'Última pregunta: ¿en qué zonas trabajás? Elegí “Todo el país” si trabajás a nivel nacional, o seleccioná zonas específicas. Podés elegir más de una zona.',
        question: {
          id: 'workZones',
          type: 'interactive',
          interactiveType: 'workZones',
        },
      };
    }

    if (lastAction === 'workZonesComplete') {
      return {
        content: '¡Perfecto! Ya tenemos tu información. Ahora, podés revisar o cambiar pasos. Si ves todo bien, podés finalizar el onboarding.',
        question: {
          id: 'completeActions',
          type: 'interactive',
          interactiveType: 'completeActions',
        },
      };
    }

    if (lastAction === 'finishOnboarding') {
      return {
        content: 'Antes de finalizar, revisá el resumen para confirmar que todo esté correcto.',
        question: {
          id: 'summary',
          type: 'interactive',
          interactiveType: 'summary',
        },
      };
    }

    if (lastAction === 'summaryEdit') {
      return {
        content: 'Podés revisar o cambiar pasos, o finalizar el onboarding para guardar tu perfil.',
        question: {
          id: 'completeActions',
          type: 'interactive',
          interactiveType: 'completeActions',
        },
      };
    }

    if (state.accountType === 'PERSON') {
      const form = state.personaForm;
      if (!form.name) {
        return {
          content: 'Escribí tu nombre',
          question: {
            id: 'name',
            type: 'text',
            field: 'name',
            placeholder: 'Escribí tu nombre...',
          },
        };
      }
      if (!form.lastName) {
        return {
          content: `¡Gracias ${form.name}! Ahora escribí tu apellido`,
          question: {
            id: 'lastName',
            type: 'text',
            field: 'lastName',
            placeholder: 'Escribí tu apellido...',
          },
        };
      }
      if (form.tel === '' || form.tel === undefined) {
        return {
          content: '¿Querés sumar un teléfono de contacto? Esto facilita que otros perfiles se comuniquen con vos. Si no, podés hacer clic en “Omitir” y continuar.',
          question: {
            id: 'tel',
            type: 'text',
            field: 'tel',
            placeholder: 'Escribí tu teléfono (solo números)...',
          },
        };
      }
      if (form.webSite === '' || form.webSite === undefined) {
        return {
          content: '¿Tenés un sitio web o portfolio? Podés agregarlo ahora o más adelante.',
          question: {
            id: 'webSite',
            type: 'text',
            field: 'webSite',
            placeholder: 'Escribí tu sitio web (opcional)...',
          },
        };
      }
      if (form.bio === '' || form.bio === undefined) {
        return {
          content: 'Contame sobre vos. Escribí qué hacés y cuál es tu experiencia. Esto ayudará a otros usuarios a conocerte mejor. Si preferís, podés hacer clic en "Omitir" para continuar',
          question: {
            id: 'bio',
            type: 'text',
            field: 'bio',
            placeholder: 'Contame sobre vos...',
          },
        };
      }
    } else if (state.accountType === 'COMPANY') {
      const form = state.empresaForm;
      if (!form.companyName) {
        return {
          content: 'Escribí el nombre de tu empresa',
          question: {
            id: 'companyName',
            type: 'text',
            field: 'companyName',
            placeholder: 'Escribí el nombre de tu empresa...',
          },
        };
      }
      if (form.tel === '' || form.tel === undefined) {
        return {
          content: '¿Querés sumar un teléfono de contacto? Esto facilita que otros perfiles se comuniquen con vos. Si no, podés hacer clic en “Omitir” y continuar.',
          question: {
            id: 'tel',
            type: 'text',
            field: 'tel',
            placeholder: 'Escribí tu teléfono (solo números)...',
          },
        };
      }
      if (form.webSite === '' || form.webSite === undefined) {
        return {
          content: '¿Tenés un sitio web o portfolio? Podés agregarlo ahora o más adelante.',
          question: {
            id: 'webSite',
            type: 'text',
            field: 'webSite',
            placeholder: 'Escribí tu sitio web (opcional)...',
          },
        };
      }
      if (form.bio === '' || form.bio === undefined) {
        return {
          content: 'Contame sobre tu empresa. Escribí en el campo de abajo qué hace tu empresa y cuál es su experiencia. Esto ayudará a otros usuarios a conocerte mejor. Si preferís, podés hacer clic en "Omitir" para continuar',
          question: {
            id: 'bio',
            type: 'text',
            field: 'bio',
            placeholder: 'Contame sobre tu empresa...',
          },
        };
      }
    }

    if (state.userSkills.length === 0) {
      return {
        content: '¡Excelente! Ahora vamos a seleccionar tus habilidades. Esto ayuda a que te encuentren los perfiles correctos.Podés buscar y elegir todas las que apliquen.',
        question: {
          id: 'skills',
          type: 'interactive',
          interactiveType: 'skills',
        },
      };
    }

    if (!state.allCountry && state.workZones.length === 0) {
      return {
        content: '¿En qué zonas trabajás? Seleccioná si comercializás en todo el país o en zonas específicas.',
        question: {
          id: 'workZones',
          type: 'interactive',
          interactiveType: 'workZones',
        },
      };
    }

    return {
      content: '¡Perfecto! Ya tenemos tu información. Ahora, podés revisar o cambiar pasos. Si ves todo bien, podés finalizar el onboarding.',
      question: {
        id: 'completeActions',
        type: 'interactive',
        interactiveType: 'completeActions',
      },
    };
  },
};
